import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  makeStyles,
  Radio,
  RadioGroup,
  Typography
} from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { LoadingButton, RepliconDialog } from '~/modules/common/components';
import { RoleAssignmentMode } from '~/types';
import TaskRolesList from './TaskRolesList';
import UserProfileRolesList from './UserProfileRolesList';
import SpecificRolesSelector, { TABS } from './SpecificRolesSelector';
import useSpecificRolesSelectorHandler from './SpecificRolesSelector/useSpecificRolesSelectorHandler';

const useStyles = makeStyles(theme => ({
  content: {
    paddingBottom: theme.spacing(1)
  },
  skeleton: {
    margin: theme.spacing(1, 0)
  },
  radioOption: {
    alignItems: 'flex-start',
    marginLeft: 0,
    marginBottom: theme.spacing(0.5)
  },
  radioControl: {
    paddingTop: theme.spacing(0.5)
  },
  optionSubtitle: {
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
    display: 'block'
  },
  selectedOptionContent: {
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(4)
  },
  userProfileMessage: {
    ...theme.typography.body2,
    color: theme.palette.text.secondary
  },
  actions: {
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(2)
  }
}));

const defaultTitle = <FormattedMessage id="selectTaskRolesDialog.title" />;

const defaultRadioOptions = [
  {
    value: RoleAssignmentMode.Inherit,
    labelId: 'selectTaskRolesDialog.taskRoles',
    subtitleId: 'selectTaskRolesDialog.taskRolesSubtitle'
  },
  {
    value: RoleAssignmentMode.UserProfileRoles,
    labelId: 'selectTaskRolesDialog.userProfileRoles',
    subtitleId: 'selectTaskRolesDialog.userProfileRolesSubtitle'
  },
  {
    value: RoleAssignmentMode.SpecificRoles,
    labelId: 'selectTaskRolesDialog.specificRoles',
    subtitleId: 'selectTaskRolesDialog.specificRolesSubtitle'
  }
];

const RoleAssignmentDialog = ({
  open,
  onClose,
  onSave,
  isSaving,
  taskUri,
  variant,
  skipAssignedRolesQuery,
  teamMemberUserId,
  initialMode,
  savedDefaultRole,
  savedRoleCount,
  title = defaultTitle,
  radioOptions = defaultRadioOptions
}) => {
  const classes = useStyles();

  const getInitialMode = () =>
    radioOptions.some(option => option.value === initialMode)
      ? initialMode
      : radioOptions[0].value;

  const isSavedSpecific = initialMode === RoleAssignmentMode.SpecificRoles;
  const savedDefault = isSavedSpecific ? savedDefaultRole : null;
  const savedCount = isSavedSpecific ? savedRoleCount ?? 0 : 0;

  const [roleAssignmentMode, setRoleAssignmentMode] = useState(getInitialMode);

  const {
    rolesToAssign,
    roleUrisToUnassign,
    isRoleChecked,
    defaultRole,
    selectedCount,
    handleToggle,
    handleSetDefault,
    reset: resetSpecificRoles
  } = useSpecificRolesSelectorHandler({
    savedDefaultRole: savedDefault,
    savedRoleCount: savedCount
  });

  const newlySelectedRoles = useMemo(() => rolesToAssign.filter(r => r.isNew), [
    rolesToAssign
  ]);

  useEffect(() => {
    if (open) {
      setRoleAssignmentMode(getInitialMode());
      resetSpecificRoles();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleModeChange = useCallback(
    e => setRoleAssignmentMode(e.target.value),
    []
  );

  const getRadioButtonLabel = useCallback(
    ({ labelId, subtitleId }) => (
      <span>
        <FormattedMessage id={labelId} />
        <span className={classes.optionSubtitle}>
          <FormattedMessage id={subtitleId} />
        </span>
      </span>
    ),
    [classes.optionSubtitle]
  );

  const handleSave = useCallback(async () => {
    const isSpecificRoles =
      roleAssignmentMode === RoleAssignmentMode.SpecificRoles;

    const shouldDemoteSavedDefault =
      isSpecificRoles &&
      savedDefault &&
      !roleUrisToUnassign.has(savedDefault.id) &&
      rolesToAssign.some(role => role.isDefault) &&
      !rolesToAssign.some(role => role.roleUri === savedDefault.id);

    await onSave({
      roleAssignmentMode,
      rolesToAssign: shouldDemoteSavedDefault
        ? [
            ...rolesToAssign,
            {
              roleUri: savedDefault.id,
              displayText: savedDefault.displayText,
              isDefault: false,
              isNew: false
            }
          ]
        : rolesToAssign,
      roleUrisToUnassign,
      defaultRole: isSpecificRoles ? defaultRole : null,
      totalRoleCount: isSpecificRoles ? selectedCount : 0
    });

    onClose();
  }, [
    roleAssignmentMode,
    rolesToAssign,
    roleUrisToUnassign,
    savedDefault,
    defaultRole,
    selectedCount,
    onSave,
    onClose
  ]);

  return (
    <RepliconDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      enableUnsavedChangesWarning
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent className={classes.content}>
        <>
          <RadioGroup value={roleAssignmentMode} onChange={handleModeChange}>
            {radioOptions.map(option => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                className={classes.radioOption}
                control={
                  <Radio
                    color="primary"
                    size="small"
                    className={classes.radioControl}
                  />
                }
                label={getRadioButtonLabel(option)}
              />
            ))}
          </RadioGroup>

          <div className={classes.selectedOptionContent}>
            {roleAssignmentMode === RoleAssignmentMode.Inherit && (
              <TaskRolesList taskUri={taskUri} />
            )}
            {roleAssignmentMode === RoleAssignmentMode.UserProfileRoles && (
              <>
                <Typography className={classes.userProfileMessage}>
                  <FormattedMessage id="selectTaskRolesDialog.userProfileRolesMessage" />
                </Typography>
                {teamMemberUserId && (
                  <UserProfileRolesList userId={teamMemberUserId} />
                )}
              </>
            )}
            {roleAssignmentMode === RoleAssignmentMode.SpecificRoles && (
              <SpecificRolesSelector
                taskUri={taskUri}
                userUri={teamMemberUserId}
                variant={variant}
                skipAssignedRolesQuery={skipAssignedRolesQuery}
                newlySelectedRoles={newlySelectedRoles}
                isRoleChecked={isRoleChecked}
                defaultRole={defaultRole}
                selectedCount={selectedCount}
                onToggle={handleToggle}
                onSetDefault={handleSetDefault}
                initialActiveTab={savedCount > 0 ? TABS.SELECTED : TABS.ALL}
              />
            )}
          </div>
        </>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button onClick={onClose} variant="contained">
          <FormattedMessage id="button.cancel" />
        </Button>
        <LoadingButton
          onClick={handleSave}
          color="primary"
          variant="contained"
          isLoading={isSaving}
          disabled={isSaving}
        >
          <FormattedMessage id="button.save" />
        </LoadingButton>
      </DialogActions>
    </RepliconDialog>
  );
};

RoleAssignmentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  taskUri: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['taskRole', 'taskResourceRole']).isRequired,
  skipAssignedRolesQuery: PropTypes.bool,
  isSaving: PropTypes.bool,
  teamMemberUserId: PropTypes.string,
  initialMode: PropTypes.string,
  savedDefaultRole: PropTypes.shape({
    id: PropTypes.string,
    displayText: PropTypes.string
  }),
  savedRoleCount: PropTypes.number,
  title: PropTypes.node,
  radioOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      labelId: PropTypes.string.isRequired,
      subtitleId: PropTypes.string.isRequired
    })
  )
};

export default RoleAssignmentDialog;
